import { useCallback, useEffect, useRef, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { clock } from "../system/format";

// ---------------------------------------------------------------------------
// D12b (0295). THE LEAD'S ROLL CALL, IN THE WORKER APP.
//
// Shown on a shift only when this booking leads its post: app.lead_crew refuses
// anybody else, and a 403 here renders nothing. Arrivals go through the same
// route a worker's own check-in does (app.may_record_attendance_for decides);
// "not coming" is the no-show edge the server names, with a reason (0294, D92-9).
// The screen spells no state, kind or reason: every id comes from the reply.
// ---------------------------------------------------------------------------

type Member = {
  assignment_id: string;
  given_name: string;
  family_name: string;
  is_supervisor: boolean;
  status_label: string;
  arrived_at: string | null;
  minutes_late: number | null;
  next_kind: string | null;
  next_kind_label: string | null;
  absent_to: string | null;
  absent_label: string | null;
};
type Reason = { id: string; label: string };

export function LeadCrew({ assignmentId, apiBase }: { assignmentId: string; apiBase: string }) {
  const { locale, t, refusal } = useT();
  const [crew, setCrew] = useState<Member[] | null>(null);
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [reason, setReason] = useState<Map<string, string>>(new Map());
  const [source, setSource] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ who: string; text: string } | null>(null);
  const keys = useRef<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        send<{ crew?: Member[]; reasons?: Reason[] }>(console_.leadCrew(apiBase, assignmentId)),
        send<{ clock_sources?: { id: string; is_authoritative: boolean }[] }>(console_.clockSources(apiBase)),
      ]);
      if (c.status !== 200 || !c.body.crew) return; // not a lead on this shift: nothing to show
      setCrew(c.body.crew);
      setReasons(c.body.reasons ?? []);
      setSource(s.body.clock_sources?.find((x) => x.is_authoritative)?.id ?? null);
    } catch {
      // No signal: the card simply does not appear; the shift above still works offline.
    }
  }, [apiBase, assignmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** One key per intent, held across retries (MyShift's rule): a double tap is one act. */
  function keyFor(intent: string): string {
    let k = keys.current.get(intent);
    if (!k) {
      k = crypto.randomUUID();
      keys.current.set(intent, k);
    }
    return k;
  }

  async function act(m: Member, intent: string, call: () => Promise<{ status: number; body: { error?: { message?: string } } }>) {
    setBusy(m.assignment_id);
    setNotice(null);
    try {
      const res = await call();
      if (res.status === 200 || res.status === 201) {
        keys.current.delete(intent);
        await load();
      } else {
        setNotice({ who: m.assignment_id, text: refusal(res.body.error) ?? t("That could not be recorded.") });
      }
    } catch {
      setNotice({ who: m.assignment_id, text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(null);
    }
  }

  if (crew === null) return null;

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Your crew")}</h2>
        <p className="muted">{t("You lead this post. Record arrivals, or say who is not coming.")}</p>
      </div>
      {/* Every Record button waits for the authoritative clock; without it they are grey, and
          the lead is told why rather than left to guess (D96-4 hid behind exactly this). */}
      {!source ? <p className="muted">{t("The clock could not be read, so nothing can be recorded. Reload to try again.")}</p> : null}
      <ul className="plain-list">
        {crew.map((m) => {
          // BLANK UNTIL THE LEAD CHOOSES (Board.tsx's rule). The first-ranked reason is "the
          // client reduced the headcount", which no lead means by "not coming" (audit 2026-10-14).
          const why = reason.get(m.assignment_id) ?? "";
          return (
            <li key={m.assignment_id} className="stack">
              <div className="row">
                <strong>{m.given_name} {m.family_name}</strong>
                {m.is_supervisor ? <span className="muted">{t("Supervisor")}</span> : null}
                <span className="muted">{m.status_label}</span>
                {m.arrived_at ? (
                  <span className="muted">
                    {t("Arrived {when}", { when: clock(m.arrived_at) })}
                    {m.minutes_late !== null && m.minutes_late > 0 ? ` ${t("({n} min late)", { n: m.minutes_late })}` : ""}
                  </span>
                ) : null}
              </div>
              <div className="row">
                {m.next_kind ? (
                  <button
                    type="button"
                    className="btn btn-quiet"
                    disabled={busy !== null || !source}
                    onClick={() =>
                      void act(m, `kind:${m.assignment_id}`, () =>
                        send(console_.recordAttendance(apiBase, m.assignment_id, {
                          kind: m.next_kind,
                          clock_source: source,
                          idempotency_key: keyFor(`kind:${m.assignment_id}`),
                        })),
                      )
                    }
                  >
                    {busy === m.assignment_id
                      ? t("Recording…")
                      : t("Record: {kind}", { kind: translateLabel(locale, m.next_kind_label ?? m.next_kind) })}
                  </button>
                ) : null}
                {m.absent_to ? (
                  <>
                    <label>
                      {t("Reason")}{" "}
                      <select
                        value={why}
                        onChange={(e) => setReason(new Map(reason).set(m.assignment_id, e.target.value))}
                      >
                        <option value="">{t("Choose a reason")}</option>
                        {reasons.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="btn btn-quiet"
                      disabled={busy !== null || why === ""}
                      onClick={() =>
                        void act(m, `absent:${m.assignment_id}`, () =>
                          send(console_.transitionAssignment(
                            apiBase,
                            m.assignment_id,
                            { to_state: m.absent_to, reason: why },
                            keyFor(`absent:${m.assignment_id}`),
                          )),
                        )
                      }
                    >
                      {translateLabel(locale, m.absent_label ?? m.absent_to)}
                    </button>
                  </>
                ) : null}
              </div>
              {notice?.who === m.assignment_id ? (
                <div className="form-message" role="status" aria-live="polite">
                  <StatusChip tone="attention" label={t("Not recorded")} />
                  <span>{notice.text}</span>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
